/* eslint-disable react-refresh/only-export-components */
import { DimeFilterButton, DimeFilterCombobox, DimeSeparator } from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import { MapPinIcon } from 'lucide-react';
import * as React from 'react';

import { FilterListDemo } from './-FilterListDemo.js';

export const Route = createFileRoute('/design-system/filter-button')({
  component: FilterButtonDocs,
});

function DocsSection({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <section className="mb-10">
      <h2 className="mb-1 text-xl font-semibold">{title}</h2>
      <DimeSeparator className="mb-4" />
      {children}
    </section>
  );
}

function Preview({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-background p-6">
      {children}
    </div>
  );
}

const DEMO_STATES: { id: string; name: string }[] = [
  { id: 'ca', name: 'California' },
  { id: 'ny', name: 'New York' },
  { id: 'tx', name: 'Texas' },
];

const STATE_OPTIONS = DEMO_STATES.map((state) => ({ value: state.id, label: state.name }));
const COMBOBOX_COPY = {
  label: 'State',
  icon: <MapPinIcon className="size-3.5" aria-hidden />,
  options: STATE_OPTIONS,
  searchPlaceholder: 'Search states…',
  emptyMessage: 'No states found.',
  optionsLabel: 'State options',
};

function MultipleComboboxDemo() {
  const [value, setValue] = React.useState<string[]>([]);
  return <DimeFilterCombobox {...COMBOBOX_COPY} multiple value={value} onValueChange={setValue} />;
}

function SingleComboboxDemo() {
  const [value, setValue] = React.useState<string | undefined>();
  return <DimeFilterCombobox {...COMBOBOX_COPY} value={value} onValueChange={setValue} />;
}

const ALL_STATES = { value: '__all_states__', label: 'All States' };

function ScopeComboboxDemo() {
  const [value, setValue] = React.useState(ALL_STATES.value);
  return (
    <DimeFilterCombobox
      {...COMBOBOX_COPY}
      label="Scope"
      optionsLabel="Scope options"
      options={[ALL_STATES, ...STATE_OPTIONS]}
      clearable={false}
      value={value}
      onValueChange={(next) => setValue(next ?? ALL_STATES.value)}
    />
  );
}

function FilterButtonDocs() {
  return (
    <div>
      <h1 className="mb-1 text-3xl font-bold">Filter Button</h1>
      <p className="mb-8 text-muted-foreground">
        Shared chrome for list-toolbar filters: outline control, optional ListFilter icon, applied
        count badge, and an accessible name that includes the count. Use{' '}
        <code>DimeFilterButton</code> for popover/menu triggers and <code>DimeFilterCombobox</code>{' '}
        for a searchable filter list; it composes <code>DimeFilterButtonContent</code> inside its
        combobox trigger for you.
      </p>

      <DocsSection title="Default Filter">
        <Preview>
          <DimeFilterButton />
          <DimeFilterButton count={2} />
        </Preview>
      </DocsSection>

      <DocsSection title="Menu trigger (chevron)">
        <p className="mb-4 text-sm text-muted-foreground">
          Locations and warehouse Users menus pass <code>showChevron</code> so the control matches
          the Columns button beside it. The label hides below <code>sm</code> whenever an icon is
          shown.
        </p>
        <Preview>
          <DimeFilterButton showChevron />
          <DimeFilterButton showChevron count={1} />
        </Preview>
      </DocsSection>

      <DocsSection title="Filter combobox">
        <p className="mb-4 text-sm text-muted-foreground">
          <code>DimeFilterCombobox</code> opens a searchable list from this button (a popover
          holding a <code>DimeFilterList</code>). With <code>multiple</code>, a Select All toggle
          beside the search box ticks every row but is sent as no filter, so the button shows its
          label with no count, then counts the picks (&ldquo;State, N applied&rdquo;). Picking one,
          the button names the pick, and picking the ticked option again clears it (unless{' '}
          <code>clearable</code> is off). Pass a <code>notice</code> for a load error or an empty
          list. Build on it rather than hand-wiring <code>DimeFilterButtonContent</code> inside a
          combobox.
        </p>
        <Preview>
          <MultipleComboboxDemo />
          <SingleComboboxDemo />
        </Preview>
      </DocsSection>

      <DocsSection title="Scope with an All option">
        <p className="mb-4 text-sm text-muted-foreground">
          For a choice that is never empty, list an &ldquo;All &hellip;&rdquo; option and pass{' '}
          <code>clearable={'{false}'}</code>: picking the ticked option again keeps it, and only the
          All option widens the choice.
        </p>
        <Preview>
          <ScopeComboboxDemo />
        </Preview>
      </DocsSection>

      <DocsSection title="Filter list">
        <p className="mb-4 text-sm text-muted-foreground">
          <code>DimeFilterList</code> is the checklist inside <code>DimeFilterCombobox</code> and
          the Filter-panel sections: a listbox whose picks are <code>aria-selected</code>, with the
          keyboard&rsquo;s row as its active descendant. Use it on its own inside a panel section;
          pair it with <code>DimeFilterSelectAllToggle</code> and <code>useFilterSelectAll</code>{' '}
          for Select All.
        </p>
        <Preview>
          <FilterListDemo />
        </Preview>
      </DocsSection>

      <DocsSection title="Props">
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/50">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Prop</th>
                <th className="px-3 py-2 text-left font-medium">Type</th>
                <th className="px-3 py-2 text-left font-medium">Notes</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['label', 'string', 'Visible label. Defaults to "Filter".'],
                ['icon', 'ReactNode | null', 'Defaults to ListFilter; pass null to hide.'],
                ['count', 'number', 'Applied-filter count; badge and aria-label omit at zero.'],
                ['showChevron', 'boolean', 'Muted size-4 ChevronDown for menu/popover triggers.'],
                [
                  'hideLabelBelowSm',
                  'boolean',
                  'Default true when an icon is shown. Pass false for text-only chips.',
                ],
                ['accessibleName', 'string', 'Overrides label in the aria-label (e.g. "Filters").'],
              ].map(([prop, type, notes]) => (
                <tr key={prop} className="border-b border-border last:border-0">
                  <td className="px-3 py-2 font-mono text-xs">{prop}</td>
                  <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{type}</td>
                  <td className="px-3 py-2 text-muted-foreground">{notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-sm text-muted-foreground">
          <code>DimeFilterCountBadge</code> is the badge alone — prefer going through{' '}
          <code>DimeFilterButton</code> / <code>DimeFilterButtonContent</code> so spacing stays
          consistent.
        </p>
      </DocsSection>
    </div>
  );
}
